import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  Button,
  Can,
  EmptyState,
  ErrorState,
  Field,
  Loading,
  Pill,
  SectionHeader,
  StatCard,
  inputClass,
} from '@/components/ui'
import {
  RAG,
  STAGES,
  STATUSES,
  formatDate,
  onHoldNote,
  ragTone,
  stageLabel,
  statusLabel,
  statusTone,
} from '@/lib/domain'
import { Permissions } from '@/lib/session'
import { openProjectWindow } from '@/features/projects/openProjectWindow'
import { NewProjectDialog } from '@/features/projects/NewProjectDialog'
import { useProjects, type ProjectFilters } from '@/features/projects/useProjects'
import { TeamBar, type TeamSelection } from '@/features/teams/TeamBar'
import { useTeams } from '@/features/teams/useTeams'
import { useInitiatives } from '@/features/initiatives/useInitiatives'

/** Filters that may arrive in the URL, so other pages can link straight to a subset.
 *
 * Every filter the page can *set* must also be listed here: the URL is the single
 * source of truth, so a key missing from this list is written by its dropdown and then
 * silently dropped on the read-back — the select shows the choice, the list ignores it.
 * That is exactly how the owner filter shipped broken. */
const URL_FILTERS = [
  'stage',
  'status',
  'archived',
  'team',
  'categoryId',
  'ragStatus',
  'owner',
  'initiativeId',
  'overdue',
  'search',
] as const

export function ProjectsPage() {
  /**
   * Filters live in the URL.
   *
   * That is what lets the status report's headline numbers link here — "Past target 2"
   * becomes `/projects?overdue=true` — and it means a filtered view can be bookmarked or
   * pasted to someone, which a `useState` filter never could.
   */
  const [params, setParams] = useSearchParams()
  const { data: teamData } = useTeams()
  const { data: initiativeData } = useInitiatives()
  const initiativeNames = useMemo(
    () => new Map((initiativeData?.initiatives ?? []).map((i) => [i.id, i.name])),
    [initiativeData],
  )
  const categoryNames = useMemo(() => {
    const map = new Map<string, string>()
    for (const team of teamData?.teams ?? []) {
      for (const category of team.categories) map.set(category.id, category.name)
    }
    return map
  }, [teamData])
  const filters = useMemo<ProjectFilters>(() => {
    const next: ProjectFilters = {}
    for (const key of URL_FILTERS) {
      const value = params.get(key)
      if (value) next[key] = value
    }
    return next
  }, [params])

  const setFilters = (next: ProjectFilters) => {
    const clean = Object.entries(next).filter(([, value]) => value && value !== 'all')
    setParams(Object.fromEntries(clean) as Record<string, string>, { replace: true })
  }

  const [creating, setCreating] = useState(false)
  const { data, isPending, error } = useProjects(filters)

  // Memoised because `?? []` builds a new array on every render, which changes the
  // identity `useMemo` below depends on and recomputes the stats every time.
  const projects = useMemo(() => data?.projects ?? [], [data])

  /**
   * The headline numbers come from the **unfiltered** portfolio.
   *
   * They used to be computed from the filtered list, which was invisible until the cards
   * became clickable: filtering to Complete would then recompute the cards against those
   * four projects and report "Active 0". A headline figure that changes when you click it
   * is worse than no headline figure.
   *
   * A second query rather than a second endpoint — TanStack caches it, and at this size
   * the whole portfolio is one small response.
   */
  const { data: everything } = useProjects({})
  // The archive is a separate query now that finished work has left the main list. Only
  // its total is used, and TanStack caches it, so it costs one small response.
  const { data: archive } = useProjects({ archived: 'true' })
  // Distinct owners across the whole portfolio, alphabetical. Reuses the query the stat
  // cards already make, so the dropdown costs nothing extra.
  const owners = useMemo(
    () =>
      [...new Set((everything?.projects ?? []).map((p) => p.ownerName).filter(Boolean))].sort(
        (a, b) => a.localeCompare(b),
      ),
    [everything],
  )

  const stats = useMemo(() => {
    const all = everything?.projects ?? []
    // The server's own count, not the length of what came back — those differed by three
    // when a page limit was quietly capping the list at 100.
    return {
      total: everything?.total ?? all.length,
      active: all.filter((p) => p.status === 'active').length,
      // The number a review actually opens with.
      atRisk: all.filter((p) => p.ragStatus === 'red' || p.ragStatus === 'amber').length,
      archived: archive?.total ?? 0,
    }
  }, [everything, archive])

  /**
   * Clicking a headline number filters the list to the projects behind it; clicking the
   * same one again clears it. Toggling matters — otherwise the only way back to the full
   * list is to hunt for the right dropdown and set it to "all".
   */
  const applyLens = (lens: ProjectFilters) => {
    const isActive = (Object.keys(lens) as (keyof ProjectFilters)[]).every(
      (key) => filters[key] === lens[key],
    )
    setFilters(isActive ? {} : { search: filters.search, ...lens })
  }
  const lensActive = (lens: ProjectFilters) =>
    (Object.keys(lens) as (keyof ProjectFilters)[]).every((key) => filters[key] === lens[key])

  const set = (key: keyof ProjectFilters) => (event: React.ChangeEvent<HTMLSelectElement>) =>
    setFilters({ ...filters, [key]: event.target.value })

  return (
    <div className="mx-auto max-w-[1400px] pt-2">
      <header className="mb-6">
        <h1 className="font-heading text-3xl text-fg">Projects</h1>
        <p className="mt-1 text-sm text-fg-muted">
          Every project, always visible. Teams are a filter, never a folder.
        </p>
      </header>

      <div className="mb-2 flex flex-wrap gap-3">
        <StatCard
          label="Projects"
          value={stats.total}
          tone="info"
          hint="Show every project"
          active={Object.keys(filters).filter((k) => filters[k as keyof ProjectFilters]).length === 0}
          onClick={() => setFilters({})}
        />
        <StatCard
          label="Active"
          value={stats.active}
          tone="green"
          hint="Show the active projects"
          active={lensActive({ status: 'active' })}
          onClick={() => applyLens({ status: 'active' })}
        />
        <StatCard
          label="Amber or red"
          value={stats.atRisk}
          tone="amber"
          hint="Show the projects that need attention"
          active={lensActive({ ragStatus: 'at_risk' })}
          onClick={() => applyLens({ ragStatus: 'at_risk' })}
        />
        {/* Finished work has left the list above; this is how you get back to it. It is
            archived, not deleted — every project keeps its history, documents and closure
            record, and still answers to a direct link. */}
        <StatCard
          label="Archived"
          value={stats.archived}
          tone="neutral"
          hint="Show completed and cancelled projects"
          active={lensActive({ archived: 'true' })}
          onClick={() => applyLens({ archived: 'true' })}
        />
      </div>
      <p className="mb-6 text-xs text-fg-muted">
        Press a number to see the projects behind it. Press it again to show everything.
        Completed and cancelled projects live in Archived and are kept, not deleted.
      </p>

      <SectionHeader
        action={
          <Can
            permission={Permissions.projectsCreate}
            // Shown, not hidden, to everyone else. A missing button reads as a missing
            // feature; a disabled one that says who can use it answers the question.
            fallback={
              <span title="Projects are created by a Portfolio Manager or an Administrator.">
                <Button variant="primary" disabled aria-describedby="new-project-hint">
                  New project
                </Button>
                <span id="new-project-hint" className="sr-only">
                  Projects are created by a Portfolio Manager or an Administrator.
                </span>
              </span>
            }
          >
            <Button variant="primary" onClick={() => setCreating(true)}>
              New project
            </Button>
          </Can>
        }
      >
        Portfolio
      </SectionHeader>

      {/* The coarse cut, above the finer filters. Selecting a team narrows everything
          below in place — nothing is hidden and "All teams" is one click away. */}
      <TeamBar
        selection={{ team: filters.team, categoryId: filters.categoryId }}
        onChange={(next: TeamSelection) => {
          const params = new URLSearchParams(window.location.search)
          if (next.team) params.set('team', next.team)
          else params.delete('team')
          if (next.categoryId) params.set('categoryId', next.categoryId)
          else params.delete('categoryId')
          setParams(params, { replace: true })
        }}
      />

      <div className="card mb-4 flex flex-wrap items-end gap-3 p-3">
        {/* Narrower than it was: with four filters beside it, a search box that takes the
            whole row pushes them onto a second line. A project name needs far less. */}
        <div className="min-w-[150px] max-w-[260px] flex-1">
          <Field label="Search">
            <input
              className={inputClass}
              placeholder="Project name"
              value={filters.search ?? ''}
              onChange={(event) =>
                setFilters({ ...filters, search: event.target.value })
              }
            />
          </Field>
        </div>
        {/* The same lens as the team bar above, in the row where people look for a filter.
            Both write the same URL key, so they can never disagree. */}
        <Field label="Team">
          <select
            className={inputClass}
            value={filters.team ?? 'all'}
            onChange={(event) =>
              // A category belongs to exactly one team, so changing team drops it.
              setFilters({ ...filters, team: event.target.value, categoryId: undefined })
            }
          >
            <option value="all">All teams</option>
            {(teamData?.teams ?? []).map((team) => (
              <option key={team.team} value={team.team}>
                {team.shortLabel}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Stage">
          <select className={inputClass} value={filters.stage ?? 'all'} onChange={set('stage')}>
            <option value="all">All stages</option>
            {STAGES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Health">
          <select
            className={inputClass}
            value={filters.ragStatus ?? 'all'}
            onChange={set('ragStatus')}
          >
            <option value="all">Any</option>
            {/* Matches the "Amber or red" card, so the dropdown can show what the card
                selected instead of going blank. */}
            <option value="at_risk">Amber or red</option>
            {RAG.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Status">
          <select className={inputClass} value={filters.status ?? 'all'} onChange={set('status')}>
            <option value="all">Any</option>
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </Field>

        {initiativeNames.size ? (
          <Field label="Initiative">
            <select
              className={inputClass}
              value={filters.initiativeId ?? 'all'}
              onChange={set('initiativeId')}
            >
              <option value="all">Any</option>
              {[...initiativeNames].map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}

        {/* Built from the owners that actually exist rather than the staff list: filtering to
            somebody who owns nothing returns an empty table and looks broken. */}
        <Field label="Owner">
          <select className={inputClass} value={filters.owner ?? 'all'} onChange={set('owner')}>
            <option value="all">Anyone</option>
            {owners.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {isPending ? (
        <Loading label="Loading projects" />
      ) : error ? (
        <ErrorState error={error} />
      ) : projects.length === 0 ? (
        <EmptyState title="No projects match these filters">
          Clear a filter, or create the first project.
        </EmptyState>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <caption className="sr-only">Projects</caption>
            <thead>
              <tr className="border-b border-border text-left">
                {['Project', 'Stage', 'Status', 'Health', 'Team', 'Owner', 'Target', 'Moved', 'Priority'].map(
                  (heading) => (
                    <th key={heading} className="micro-label px-4 py-2 text-fg-muted">
                      {heading}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => (
                <tr
                  key={project.id}
                  className="cursor-pointer border-b border-border last:border-0 hover:bg-canvas"
                  onClick={() => openProjectWindow(project.id)}
                >
                  <td className="px-4 py-2">
                    <div className="flex max-w-[360px] items-center gap-2">
                      {/* A real link too, so Ctrl-click and middle-click open a tab as people
                          expect; a plain click opens the project's own window. */}
                      <a
                        href={`/projects/${project.id}`}
                        className="truncate font-semibold text-fg hover:underline"
                        onClick={(event) => {
                          if (event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0) {
                            event.stopPropagation()
                            return
                          }
                          event.preventDefault()
                        }}
                      >
                        {project.name}
                      </a>
                      {/* The WIG's only footprint on this list: a badge. The discipline
                          itself lives on the WIG board. */}
                      {project.isWig ? (
                        <span
                          className="micro-label shrink-0 rounded-pill px-1.5 py-0.5"
                          style={{ color: 'var(--brand-on)', backgroundColor: 'var(--brand)' }}
                          title="Run as a Wildly Important Goal"
                        >
                          WIG
                        </span>
                      ) : null}
                    </div>
                    <div className="text-xs text-fg-muted">
                      {project.percentComplete}% complete
                      {project.initiativeId && initiativeNames.get(project.initiativeId)
                        ? ` · ${initiativeNames.get(project.initiativeId)}`
                        : ''}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-fg-muted">{stageLabel(project.stage)}</td>
                  <td className="px-4 py-2">
                    <Pill tone={statusTone(project.status)}>{statusLabel(project.status)}</Pill>
                    {onHoldNote(project) ? (
                      <div className="mt-0.5 text-[11px] text-fg-muted">{onHoldNote(project)}</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-2">
                    {project.ragStatus === 'none' ? (
                      <span className="text-fg-muted">—</span>
                    ) : (
                      <Pill tone={ragTone(project.ragStatus)}>{project.ragStatus}</Pill>
                    )}
                  </td>
                  <td className="px-4 py-2 text-fg-muted">
                    {project.teamShortLabel}
                    {project.categoryId && categoryNames.get(project.categoryId) ? (
                      <span className="text-xs">
                        {' · '}
                        {categoryNames.get(project.categoryId)}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 text-fg-muted">{project.ownerName || '—'}</td>
                  <td className="px-4 py-2 text-fg-muted">{formatDate(project.targetDate)}</td>
                  {/* Blank when the target has never moved. A zero on every row would
                      bury the handful that have actually slipped. */}
                  <td className="px-4 py-2 text-fg-muted">
                    {project.targetMovement ? (
                      <span
                        title={`Target moved ${project.targetMovement.moves} time(s), ${project.targetMovement.days} days net`}
                        style={{
                          color:
                            project.targetMovement.days > 0 ? "var(--status-amber)" : undefined,
                        }}
                      >
                        {project.targetMovement.moves}× ·{" "}
                        {project.targetMovement.days > 0 ? "+" : ""}
                        {project.targetMovement.days}d
                      </span>
                    ) : (
                      <span className="text-fg-muted">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {/* Unscored is stated plainly rather than shown as zero, which would
                        read as "lowest priority" and be untrue. */}
                    {project.scores.priority === null ? (
                      <span className="text-xs text-fg-muted">Unscored</span>
                    ) : (
                      <span className="font-semibold text-fg">{project.scores.priority}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating ? <NewProjectDialog onClose={() => setCreating(false)} /> : null}
    </div>
  )
}
